/**
 * /agents/agents/:agentId — configure one agent. Type-aware config editor
 * (phishing-monitor vs generic-llm), schedule, enable toggle, "Run now",
 * delete, and a recent-runs list. Save sends an updateAgent patch.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  HStack,
  VStack,
  Button,
  IconButton,
  Switch,
  Input,
  Textarea,
  Select,
  NumberInput,
  NumberInputField,
  FormControl,
  FormLabel,
  FormHelperText,
  SimpleGrid,
  Divider,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Center,
  Badge,
  Tag,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { FiPlay, FiTrash2, FiSave, FiArrowLeft } from 'react-icons/fi';
import { callAgents } from '../../hooks/useAgents';
import AgentStatusChip from '../../components/Agents/AgentStatusChip';

const fmtDate = (s) => (s ? new Date(s).toLocaleString() : '—');
const toLines = (arr) => (Array.isArray(arr) ? arr.join('\n') : '');
const fromLines = (s) => String(s || '').split(/[\n,]+/).map((x) => x.trim()).filter(Boolean);

// Fallback catalog used until listTypes responds (ids mirror the backend's
// MODEL_CATALOG in functions/Agents/handlers/index.js — that is the source of
// truth; this only prevents an empty dropdown on first paint).
const DEFAULT_CATALOG = {
  gemini: { label: 'Gemini (Google)', defaultModel: 'gemini-3.5-flash', topModel: 'gemini-4-argon', models: [{ id: 'gemini-3.5-flash', label: 'Gemini 3.5 Flash — workhorse (default)' }, { id: 'gemini-4-argon', label: 'Gemini 4 Argon — flagship' }] },
  openai: { label: 'OpenAI', defaultModel: 'gpt-4o-mini', topModel: 'gpt-5.5', models: [{ id: 'gpt-4o-mini', label: 'GPT-4o mini — cheap workhorse (default)' }, { id: 'gpt-5.5', label: 'GPT-5.5 — flagship' }] },
  anthropic: { label: 'Anthropic (Claude)', defaultModel: 'claude-haiku-4-5-20251001', topModel: 'claude-opus-5-5', models: [{ id: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5 — fastest / cheapest (default)' }, { id: 'claude-opus-5-5', label: 'Claude Opus 5.5 — flagship' }] },
};

/** <option> list for every provider in the catalog. */
const providerOptions = (catalog) => Object.entries(catalog).map(([id, p]) => (
  <option key={id} value={id}>{p.label || id}</option>
));

/** Model dropdown for one provider; keeps unknown saved ids selectable. */
function ModelSelect({ catalog, provider, value, onChange, defaultLabel = null }) {
  const prov = catalog[provider] || catalog.gemini;
  const known = prov.models.some((m) => m.id === value);
  return (
    <Select value={value || ''} onChange={(e) => onChange(e.target.value)}>
      <option value="">{defaultLabel || `Provider default (${prov.defaultModel})`}</option>
      {!known && value && <option value={value}>{value} (saved)</option>}
      {prov.models.map((m) => (
        <option key={m.id} value={m.id}>{m.label}</option>
      ))}
    </Select>
  );
}

export default function AgentDetailPage() {
  const { agentId } = useParams();
  const history = useHistory();
  const { url } = useRouteMatch();
  const sectionUrl = url.replace(/\/agents\/[^/]+$/, '');
  const toast = useToast();

  const [agent, setAgent] = useState(null);
  const [recentRuns, setRecentRuns] = useState([]);
  const [stats, setStats] = useState(null);
  const [catalog, setCatalog] = useState(DEFAULT_CATALOG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);

  // editable local copy
  const [name, setName] = useState('');
  const [scheduleMinutes, setScheduleMinutes] = useState(15);
  const [config, setConfig] = useState({});

  const pageBg = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorder = useColorModeValue('gray.200', 'gray.700');
  const subtle = useColorModeValue('gray.600', 'gray.400');
  // Hoisted danger-banner palettes (hooks can't be called conditionally in JSX).
  const dangerBg = {
    red: useColorModeValue('red.50', 'red.900'),
    orange: useColorModeValue('orange.50', 'orange.900'),
    yellow: useColorModeValue('yellow.50', 'yellow.900'),
    green: useColorModeValue('green.50', 'green.900'),
  };
  const dangerText = {
    red: useColorModeValue('red.800', 'red.100'),
    orange: useColorModeValue('orange.800', 'orange.100'),
    yellow: useColorModeValue('yellow.800', 'yellow.100'),
    green: useColorModeValue('green.800', 'green.100'),
  };
  const dangerBorder = {
    red: 'red.300',
    orange: 'orange.300',
    yellow: 'yellow.300',
    green: 'green.300',
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [res, typesRes] = await Promise.all([
        callAgents('getAgent', { id: agentId }),
        callAgents('listTypes').catch(() => null), // dropdowns fall back to DEFAULT_CATALOG
      ]);
      setAgent(res.agent);
      setRecentRuns(res.recentRuns || []);
      setStats(res.stats || null);
      setName(res.agent.name || '');
      setScheduleMinutes(res.agent.schedule_minutes || 15);
      setConfig(res.agent.config || {});
      if (typesRes?.models) setCatalog(typesRes.models);
    } catch (err) {
      toast({ title: 'Load failed', description: err.message, status: 'error', duration: 6000 });
    } finally {
      setLoading(false);
    }
  }, [agentId, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const setCfg = (key, value) => setConfig((c) => ({ ...c, [key]: value }));
  const setNested = (key, subKey, value) => setConfig((c) => ({ ...c, [key]: { ...(c[key] || {}), [subKey]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      await callAgents('updateAgent', { id: agentId, patch: { name, scheduleMinutes: Number(scheduleMinutes) || 15, config } });
      toast({ title: 'Saved', status: 'success', duration: 2000 });
      await load();
    } catch (err) {
      toast({ title: 'Save failed', description: err.message, status: 'error', duration: 6000 });
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (enabled) => {
    setBusy(true);
    try {
      await callAgents('toggleAgent', { id: agentId, enabled });
      toast({ title: enabled ? 'Enabled' : 'Disabled', status: 'success', duration: 2000 });
      await load();
    } catch (err) {
      toast({ title: 'Toggle failed', description: err.message, status: 'error', duration: 6000 });
    } finally {
      setBusy(false);
    }
  };

  const runNow = async () => {
    setBusy(true);
    try {
      const res = await callAgents('runNow', { id: agentId });
      toast({ title: 'Run started', status: 'success', duration: 2000 });
      history.push(`${sectionUrl}/runs/${res.runId}`);
    } catch (err) {
      toast({ title: 'Run failed', description: err.message, status: 'error', duration: 6000 });
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Delete this agent and all its runs/findings? This cannot be undone.')) return;
    setBusy(true);
    try {
      await callAgents('deleteAgent', { id: agentId });
      toast({ title: 'Agent deleted', status: 'info', duration: 2500 });
      history.push(`${sectionUrl}/agents`);
    } catch (err) {
      toast({ title: 'Delete failed', description: err.message, status: 'error', duration: 6000 });
      setBusy(false);
    }
  };

  const isPhishing = agent?.type === 'phishing-monitor';
  const isGeneric = agent?.type === 'generic-llm';

  const dangerHint = useMemo(() => {
    if (!isPhishing) return null;
    const mode = config.actionMode || 'report';
    if (mode === 'trash') return { color: 'red', text: 'This agent will TRASH high-confidence phishing across all inboxes.' };
    if (mode === 'quarantine') return { color: 'orange', text: 'This agent will remove phishing from inboxes (quarantine).' };
    if (mode === 'label') return { color: 'yellow', text: 'This agent will add a warning label to flagged mail.' };
    return { color: 'green', text: 'Report-only: writes findings, takes no action on mail.' };
  }, [isPhishing, config.actionMode]);

  if (loading || !agent) {
    return <Center py={20}><Spinner color="blue.400" /></Center>;
  }

  return (
    <Box bg={pageBg} minH="100%" p={6}>
      <HStack mb={4} spacing={3}>
        <IconButton aria-label="Back" icon={<FiArrowLeft />} size="sm" variant="ghost" onClick={() => history.push(`${sectionUrl}/agents`)} />
        <Box flex={1}>
          <HStack>
            <Heading size="md">{agent.name}</Heading>
            <Badge colorScheme="gray">{agent.type}</Badge>
            <AgentStatusChip value={agent.enabled ? 'done' : 'queued'} />
          </HStack>
          <Text fontSize="xs" color={subtle}>{agent.slug} · created {fmtDate(agent.created_at)}</Text>
        </Box>
        <HStack>
          <Text fontSize="sm" color={subtle}>Enabled</Text>
          <Switch isChecked={agent.enabled} colorScheme="green" isDisabled={busy} onChange={(e) => toggle(e.target.checked)} />
          <Button size="sm" leftIcon={<FiPlay />} colorScheme="blue" variant="outline" onClick={runNow} isLoading={busy}>Run now</Button>
        </HStack>
      </HStack>

      {dangerHint && (
        <Box mb={4} p={3} borderWidth="1px" borderRadius="md" borderColor={dangerBorder[dangerHint.color]} bg={dangerBg[dangerHint.color]}>
          <Text fontSize="sm" color={dangerText[dangerHint.color]}>{dangerHint.text}</Text>
        </Box>
      )}

      <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={6}>
        {/* Config */}
        <Box gridColumn={{ lg: '1 / 3' }} bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" p={5}>
          <Heading size="sm" mb={4}>Configuration</Heading>
          <VStack align="stretch" spacing={4}>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl>
                <FormLabel fontSize="sm">Name</FormLabel>
                <Input value={name} onChange={(e) => setName(e.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Run every (minutes)</FormLabel>
                <NumberInput min={5} value={scheduleMinutes} onChange={(_, n) => setScheduleMinutes(n || 15)}>
                  <NumberInputField />
                </NumberInput>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Model provider</FormLabel>
                <Select
                  value={config.provider || 'gemini'}
                  onChange={(e) => setConfig((c) => ({ ...c, provider: e.target.value, model: '' }))}
                >
                  {providerOptions(catalog)}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Model</FormLabel>
                <ModelSelect catalog={catalog} provider={config.provider || 'gemini'} value={config.model || ''} onChange={(v) => setCfg('model', v)} />
              </FormControl>
            </SimpleGrid>

            {isPhishing && (
              <>
                <Divider />
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl>
                    <FormLabel fontSize="sm">Action mode</FormLabel>
                    <Select value={config.actionMode || 'report'} onChange={(e) => setCfg('actionMode', e.target.value)}>
                      <option value="report">Report only (no mailbox changes)</option>
                      <option value="label">Label flagged mail</option>
                      <option value="quarantine">Quarantine phishing (remove from inbox)</option>
                      <option value="trash">Trash high-confidence phishing</option>
                    </Select>
                    <FormHelperText>Actions only fire above the confidence threshold.</FormHelperText>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Confidence threshold ({Number(config.confidenceThreshold ?? 0.85).toFixed(2)})</FormLabel>
                    <NumberInput min={0} max={1} step={0.05} value={config.confidenceThreshold ?? 0.85} onChange={(_, n) => setCfg('confidenceThreshold', Number.isFinite(n) ? n : 0.85)}>
                      <NumberInputField />
                    </NumberInput>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Scan window (hours, first scan)</FormLabel>
                    <NumberInput min={1} max={168} value={config.scanWindowHours ?? 24} onChange={(_, n) => setCfg('scanWindowHours', n || 24)}>
                      <NumberInputField />
                    </NumberInput>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Max messages / mailbox / run</FormLabel>
                    <NumberInput min={1} max={200} value={config.maxMessagesPerMailbox ?? 40} onChange={(_, n) => setCfg('maxMessagesPerMailbox', n || 40)}>
                      <NumberInputField />
                    </NumberInput>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Mailbox scope</FormLabel>
                    <Select value={config.mailboxScope?.mode || 'all'} onChange={(e) => setNested('mailboxScope', 'mode', e.target.value)}>
                      <option value="all">All active @future.loans inboxes</option>
                      <option value="list">Only specific inboxes</option>
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Warning label name</FormLabel>
                    <Input value={config.labelName || 'Suspected Phishing'} onChange={(e) => setCfg('labelName', e.target.value)} />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Fallback provider (if the primary model fails)</FormLabel>
                    <Select value={config.fallbackProvider || ''} onChange={(e) => setCfg('fallbackProvider', e.target.value)}>
                      <option value="">Auto</option>
                      {providerOptions(catalog)}
                      <option value="none">None (heuristics only on failure)</option>
                    </Select>
                  </FormControl>
                </SimpleGrid>

                {config.mailboxScope?.mode === 'list' && (
                  <FormControl>
                    <FormLabel fontSize="sm">Scoped inboxes (one per line)</FormLabel>
                    <Textarea rows={3} value={toLines(config.mailboxScope?.emails)} onChange={(e) => setNested('mailboxScope', 'emails', fromLines(e.target.value))} placeholder="jane@future.loans" />
                  </FormControl>
                )}

                <FormControl>
                  <FormLabel fontSize="sm">Excluded inboxes (never scanned — e.g. departed users whose Workspace account is gone, one per line)</FormLabel>
                  <Textarea rows={2} value={toLines(config.mailboxScope?.exclude)} onChange={(e) => setNested('mailboxScope', 'exclude', fromLines(e.target.value))} placeholder="departed-user@future.loans" />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm">Allowlisted senders (never flagged — one per line, email or domain)</FormLabel>
                  <Textarea rows={2} value={toLines(config.excludeSenders)} onChange={(e) => setCfg('excludeSenders', fromLines(e.target.value))} placeholder="noreply@uwm.com\nchase.com" />
                </FormControl>

                <Divider />
                <Box>
                  <FormControl display="flex" alignItems="center" mb={2}>
                    <Switch
                      isChecked={config.verifier?.enabled !== false}
                      colorScheme="purple"
                      onChange={(e) => setNested('verifier', 'enabled', e.target.checked)}
                      mr={3}
                    />
                    <FormLabel fontSize="sm" mb={0}>Verify before acting (second model double-checks every finding)</FormLabel>
                  </FormControl>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl isDisabled={config.verifier?.enabled === false}>
                      <FormLabel fontSize="sm">Verifier provider</FormLabel>
                      <Select value={config.verifier?.provider || 'gemini'} onChange={(e) => setConfig((c) => ({ ...c, verifier: { ...(c.verifier || {}), provider: e.target.value, model: '' } }))}>
                        {providerOptions(catalog)}
                      </Select>
                    </FormControl>
                    <FormControl isDisabled={config.verifier?.enabled === false}>
                      <FormLabel fontSize="sm">Verifier model</FormLabel>
                      <ModelSelect
                        catalog={catalog}
                        provider={config.verifier?.provider || 'gemini'}
                        value={config.verifier?.model || ''}
                        onChange={(v) => setNested('verifier', 'model', v)}
                        defaultLabel={`Top tier (${(catalog[config.verifier?.provider || 'gemini'] || catalog.gemini).topModel})`}
                      />
                    </FormControl>
                  </SimpleGrid>
                  <Text fontSize="xs" color={subtle} mt={2}>
                    Runs only when a label/quarantine/trash action is about to fire — free in report mode. The verifier can only reduce an action, never escalate.
                    If it disagrees or can't be reached, the action is withheld and the finding is recorded for manual review.
                  </Text>
                </Box>

                <Divider />
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl display="flex" alignItems="center">
                    <Switch isChecked={!!config.notify?.digest} colorScheme="blue" onChange={(e) => setNested('notify', 'digest', e.target.checked)} mr={3} />
                    <FormLabel fontSize="sm" mb={0}>Email a digest of high-risk findings</FormLabel>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Digest email</FormLabel>
                    <Input value={config.notify?.digestEmail || ''} onChange={(e) => setNested('notify', 'digestEmail', e.target.value)} placeholder="you@future.loans" />
                  </FormControl>
                </SimpleGrid>
              </>
            )}

            {isGeneric && (
              <>
                <Divider />
                <FormControl>
                  <FormLabel fontSize="sm">System prompt</FormLabel>
                  <Textarea rows={2} value={config.systemPrompt || ''} onChange={(e) => setCfg('systemPrompt', e.target.value)} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Prompt (runs every schedule)</FormLabel>
                  <Textarea rows={5} value={config.prompt || ''} onChange={(e) => setCfg('prompt', e.target.value)} placeholder="Summarize anything notable..." />
                </FormControl>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl>
                    <FormLabel fontSize="sm">Temperature</FormLabel>
                    <NumberInput min={0} max={2} step={0.1} value={config.temperature ?? 0.4} onChange={(_, n) => setCfg('temperature', Number.isFinite(n) ? n : 0.4)}>
                      <NumberInputField />
                    </NumberInput>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Max output tokens</FormLabel>
                    <NumberInput min={64} max={8192} value={config.maxOutputTokens ?? 1024} onChange={(_, n) => setCfg('maxOutputTokens', n || 1024)}>
                      <NumberInputField />
                    </NumberInput>
                  </FormControl>
                </SimpleGrid>
              </>
            )}

            <HStack justify="space-between" pt={2}>
              <Button leftIcon={<FiTrash2 />} size="sm" variant="ghost" colorScheme="red" onClick={remove} isDisabled={busy}>Delete agent</Button>
              <Button leftIcon={<FiSave />} size="sm" colorScheme="blue" onClick={save} isLoading={saving}>Save changes</Button>
            </HStack>
          </VStack>
        </Box>

        {/* Side: stats + recent runs */}
        <VStack align="stretch" spacing={6}>
          <Box bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" p={5}>
            <Heading size="sm" mb={3}>Findings</Heading>
            <HStack spacing={4} flexWrap="wrap">
              <Tag colorScheme="orange">{stats?.new || 0} new</Tag>
              <Tag colorScheme="gray">{stats?.total || 0} total</Tag>
              <Tag colorScheme="red">{stats?.last24h || 0} in 24h</Tag>
            </HStack>
            <Button mt={4} size="xs" variant="outline" w="full" onClick={() => history.push(`${sectionUrl}/findings?agentId=${agent.id}`)}>View findings</Button>
          </Box>

          <Box bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" p={5}>
            <Heading size="sm" mb={3}>Recent runs</Heading>
            <Table size="sm">
              <Thead><Tr><Th>Status</Th><Th>Trigger</Th><Th>When</Th></Tr></Thead>
              <Tbody>
                {recentRuns.map((r) => (
                  <Tr key={r.id} _hover={{ bg: cardBorder, cursor: 'pointer' }} onClick={() => history.push(`${sectionUrl}/runs/${r.id}`)}>
                    <Td><AgentStatusChip value={r.status} /></Td>
                    <Td fontSize="xs">{r.trigger_source}</Td>
                    <Td fontSize="xs">{fmtDate(r.created_at)}</Td>
                  </Tr>
                ))}
                {!recentRuns.length && <Tr><Td colSpan={3}><Text fontSize="sm" color={subtle} py={4}>No runs yet.</Text></Td></Tr>}
              </Tbody>
            </Table>
          </Box>
        </VStack>
      </SimpleGrid>
    </Box>
  );
}
